import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { notificationEventQueryKeys } from '../notification-events/notification-events.query'
import {
  createNotificationRecipient,
  deleteNotificationRecipient,
  getNotificationRecipient,
  getNotificationRecipients,
  sendNotificationRecipientVerification,
  updateNotificationRecipient,
  type NotificationRecipientIndexKeyEntries,
  type NotificationRecipientShowKeyEntries,
  type NotificationRecipientTarget,
} from './notification-recipients.api'
import type {
  CreateNotificationRecipientInput,
  NotificationRecipientIdInput,
  UpdateNotificationRecipientInput,
} from './notification-recipients.types'

/** Hooks default to the current route's organization. */
interface OrganizationScopeInput {
  organizationId?: OrganizationIdInput
}

/** Query builders normalize raw route ids before transport keys reach the API. */
interface RequiredOrganizationScopeInput {
  organizationId: OrganizationIdInput
}

type NotificationRecipientQueryTarget = Omit<NotificationRecipientTarget, 'organizationId'> &
  RequiredOrganizationScopeInput

/** The key entries each fetcher reads; the scoped key builders below set them. */
type NotificationRecipientIndexQueryKey = [AdminQueryRoot & NotificationRecipientIndexKeyEntries]
type NotificationRecipientShowQueryKey = [AdminQueryRoot & NotificationRecipientShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const notificationRecipientQueryKeys = {
  all: ({ organizationId }: OrganizationScopeInput = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'notificationRecipient' }, { organizationId }),
  ],
  index: ({ organizationId }: RequiredOrganizationScopeInput): NotificationRecipientIndexQueryKey =>
    buildScopedAdminQueryKey(notificationRecipientQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
  show: ({
    organizationId,
    recipientId,
  }: NotificationRecipientQueryTarget): NotificationRecipientShowQueryKey =>
    buildScopedAdminQueryKey(notificationRecipientQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      recipientId: Number(recipientId),
    }),
}

function notificationRecipientIndexQuery({ organizationId }: RequiredOrganizationScopeInput) {
  return indexTableQueryOptions({
    queryKey: notificationRecipientQueryKeys.index({ organizationId }),
    queryFn: getNotificationRecipients,
  })
}

export const notificationRecipientQueries = {
  index: notificationRecipientIndexQuery,

  show: ({ organizationId, recipientId }: NotificationRecipientQueryTarget) =>
    queryOptions({
      queryKey: notificationRecipientQueryKeys.show({ organizationId, recipientId }),
      queryFn: getNotificationRecipient,
      initialData: () =>
        queryClient
          .getQueryData(notificationRecipientIndexQuery({ organizationId }).queryKey)
          ?.find((recipient) => recipient.id === Number(recipientId)),
      initialDataUpdatedAt: () =>
        queryClient.getQueryState(notificationRecipientQueryKeys.index({ organizationId }))
          ?.dataUpdatedAt,
    }),
}

function invalidateRecipientQueries(organizationId: number) {
  return queryClient.invalidateQueries({
    queryKey: notificationRecipientQueryKeys.all({ organizationId }),
  })
}

function invalidateRecipientEventQueries(organizationId: number) {
  return queryClient.invalidateQueries({
    queryKey: notificationEventQueryKeys.all({ organizationId }),
  })
}

function invalidateRecipientAndEventQueries(organizationId: number) {
  return Promise.all([
    invalidateRecipientQueries(organizationId),
    invalidateRecipientEventQueries(organizationId),
  ])
}

export function useNotificationRecipients({
  organizationId,
  ...params
}: OrganizationScopeInput &
  QueryParams<ReturnType<typeof notificationRecipientQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...notificationRecipientQueries.index({
      organizationId: resolvedOrganizationId,
    }),
    ...params,
  })
}

export function useNotificationRecipient(
  recipientId: NotificationRecipientIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScopeInput & QueryParams<ReturnType<typeof notificationRecipientQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...notificationRecipientQueries.show({
      organizationId: resolvedOrganizationId,
      recipientId,
    }),
    ...params,
  })
}

export function useCreateNotificationRecipientMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CreateNotificationRecipientInput) =>
      createNotificationRecipient({ organizationId, ...values }),
    onSuccess: () => invalidateRecipientQueries(organizationId),
  })
}

export function useUpdateNotificationRecipientMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateNotificationRecipientInput) =>
      updateNotificationRecipient({ organizationId, ...values }),
    onSuccess: () => invalidateRecipientAndEventQueries(organizationId),
  })
}

const cleanupDeletedNotificationRecipientQueries = ({
  organizationId,
  recipientId,
}: NotificationRecipientTarget) => {
  queryClient.removeQueries({
    queryKey: notificationRecipientQueryKeys.show({
      organizationId,
      recipientId,
    }),
  })

  return invalidateRecipientAndEventQueries(organizationId)
}

export function useDeleteNotificationRecipientMutation({
  cleanupOnSuccess = true,
}: { cleanupOnSuccess?: boolean } = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedNotificationRecipient = (recipientId: NotificationRecipientIdInput) =>
    cleanupDeletedNotificationRecipientQueries({ organizationId, recipientId })

  const mutation = useMutation({
    mutationFn: (recipientId: NotificationRecipientIdInput) =>
      deleteNotificationRecipient({ organizationId, recipientId }),
    onSuccess: (_, recipientId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedNotificationRecipient(recipientId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedNotificationRecipient,
  }
}

export function useSendRecipientVerificationMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (recipientId: NotificationRecipientIdInput) =>
      sendNotificationRecipientVerification({ organizationId, recipientId }),
    onSettled: () => invalidateRecipientQueries(organizationId),
  })
}
